import { useContext, useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Select,
  Checkbox,
  Row,
  Col,
  Tag,
  Button,
  Card,
  Flex,
  Divider,
} from "antd";

import { UserContext } from "@/context/UserContext";

import {
  isTaskStatusFinal,
  PRIORITY_OPTIONS,
  TASK_TYPE_OPTIONS,
} from "@/constants/tasks";

import dayjs from "dayjs";

import EyeIcon from "@/icons/EyeIcon";
import EyeOffIcon from "@/icons/EyeOffIcon";

import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";
import AssignTo from "@/components/ui/AssignTo";
import {
  MarkdownEditor,
  normalizeDescriptionMarkdown,
} from "@/components/ui/MarkdownText";

import { getUnitEmployees } from "@/api/employees";
import { getTasksOptions } from "@/api/tasks";

function TaskTemplateForm({
  form,
  data,
  priorities = [],
  taskTypes = [],
  units = [],
  isAdmins = false,
  formSize = "large", // "large" || "small"
  task = "",
  showSubmit = false,
  onSubmit,
  formId,
}) {
  const { user } = useContext(UserContext);
  const [internalForm] = Form.useForm();
  const usedForm = form || internalForm;

  const [buttonDisabled, setButtonDisabled] = useState(true);

  const [showDetails, setShowDetails] = useState(true);
  const [useNewData, setUseNewData] = useState(false);

  const [isExecutor, setIsExecutor] = useState(
    user?.employee_uuid === data?.responsible_executor_uuid
  );
  const [isAcceptor, setIsAcceptor] = useState(
    user?.employee_uuid === data?.assignee_acceptor_uuid
  );

  const [executorsOptions, setExecutorOptions] = useState([]);
  const [soExecutorsOptions, setSoExecutorOptions] = useState([]);
  const [tasksOptions, setTasksOptions] = useState([]);

  const prioritiesOptions = useMemo(() => {
    return priorities.filter(({ deleted }) => !deleted);
  }, [priorities]);

  const taskTypesOptions = useMemo(() => {
    return taskTypes.filter(({ deleted }) => !deleted);
  }, [taskTypes]);

  const defaultPriority = prioritiesOptions.find(
    ({ is_default }) => is_default
  );
  const defaultUnit = units[0];
  const defaultTaskType = useMemo(() => {
    return taskTypesOptions.find(({ is_default }) => is_default) || null;
  }, [taskTypesOptions]);

  // При инициализации компонента запрашиваем список исполнителей
  useEffect(() => {
    fetchUserList(data?.unit_uuid || defaultUnit.uuid);
    fetchTaskList();

    // eslint-disable-next-line
  }, [defaultUnit, data]);

  const mapMembersToOptions = (members) => {
    return members.map((item) => {
      const { uuid, lastname, firstname, patronymic } = item;
      return {
        key: uuid,
        value: uuid,
        label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
        ...item,
      };
    });
  };

  const ensureTaskInOptions = (taskObj) => {
    if (!taskObj?.uuid) return;
    setTasksOptions((prev) => {
      if (prev.some((t) => t.uuid === taskObj.uuid)) return prev;
      const opt = {
        key: taskObj.uuid,
        value: taskObj.uuid,
        label: `№${taskObj.task_number} -  ${taskObj.name}`,
        ...taskObj,
      };
      return [opt, ...prev];
    });
  };

  const fillFromTask = async (taskObj) => {
    if (!taskObj) return;

    const parent =
      taskObj.parent_task_uuid &&
        tasksOptions.some((t) => t.uuid === taskObj.parent_task_uuid)
        ? taskObj.parent_task_uuid
        : null;

    // обновим списки сотрудников под выбранную РГ
    if (taskObj.unit_uuid) {
      await fetchUserList(taskObj.unit_uuid);
    } else if (defaultUnit?.uuid) {
      await fetchUserList(defaultUnit?.uuid);
    }

    usedForm.setFieldsValue({
      task_uuid: taskObj.uuid,
      name: taskObj.name || "",
      description: taskObj.description || "",
      unit_uuid: taskObj.unit_uuid || defaultUnit?.uuid || null,
      start_date: taskObj.start_date ? dayjs(taskObj.start_date) : null,
      due_date: taskObj.due_date ? dayjs(taskObj.due_date) : null,
      responsible_executor_uuid: taskObj.responsible_executor_uuid || null,
      assignee_acceptor_uuid: taskObj.assignee_acceptor_uuid || null,
      importance_uuid: taskObj.importance_uuid || defaultPriority?.uuid || null,
      task_type_uuid: taskObj.task_type_uuid || defaultTaskType?.uuid || null,
      coexecutors_uuids: taskObj.coexecutors?.map((c) => c.uuid) || [],
      parent_task_uuid: parent,
      author_uuid: user.employee_uuid,
      is_accessible_for_all_in_unit: taskObj.is_accessible_for_all_in_unit,
      is_visible: true,
      planned_costs: null,
      require_actuals_on_acceptance:
        taskObj.require_actuals_on_acceptance || false,
    });

    setIsExecutor(taskObj.responsible_executor_uuid === user.employee_uuid);
    setIsAcceptor(taskObj.assignee_acceptor_uuid === user.employee_uuid);
  };

  const fetchUserList = async (unit_uuid) => {
    try {
      const { members } = await getUnitEmployees({
        ...(unit_uuid && { unit_uuid }),
      });

      // Fixme: Может уже придумаем другой способ как обходить удаленных из РГ сотрудников?
      const authors = [...members];
      const executors = [...members];
      const coexecutors = [...members];

      if (
        data?.author &&
        !authors.find((item) => item.uuid === data.author.uuid)
      ) {
        authors.push(data.author);
      }

      if (
        data?.executor &&
        !executors.find((item) => item.uuid === data.executor.uuid)
      ) {
        executors.push(data.executor);
      }

      if (data?.coexecutors?.length) {
        data.coexecutors.forEach((coexecutor) => {
          if (!coexecutors.find((member) => member.uuid === coexecutor.uuid)) {
            coexecutors.push(coexecutor);
          }
        });
      }

      // setAuthorsOptions(mapMembersToOptions(authors));
      setExecutorOptions(mapMembersToOptions(executors));
      setSoExecutorOptions(mapMembersToOptions(coexecutors));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTaskList = async () => {
    try {
      const { tasks } = await getTasksOptions({}, isAdmins);

      // Если пришла родительская задача, но ее нет в списке, добавляем из поля parent в котором пришли минимальные данные
      const hasHiddenParent =
        data?.parent_task_uuid &&
        !tasks.find((item) => item.uuid === data.parent_task_uuid);

      const parent = data?.parent;

      setTasksOptions([
        ...(hasHiddenParent
          ? [
            {
              key: parent.uuid,
              value: parent.uuid,
              label: `№${parent.task_number} -  ${parent.name}`,
              ...parent,
            },
          ]
          : []),
        ...tasks
          .filter(({ status }) => !isTaskStatusFinal(status))
          .map((item) => ({
            key: item.uuid,
            value: item.uuid,
            label: `№${item.task_number} -  ${item.name}`,
            ...item,
          })),
      ]);
    } catch (error) {
      console.log(error);

      if (data?.parent) {
        return [
          {
            ...data.parent,
            label: `№${data.parent.task_number} -  ${data.parent.name}`,
          },
        ];
      } else {
        usedForm.setFieldValue("parent_task_uuid", null);
      }
    }
  };

  useEffect(() => {
    if (data?.uuid) return; // при редактировании не трогаем

    if (!task) return;

    if (typeof task === "object" && task.uuid) {
      ensureTaskInOptions(task);
      usedForm.setFieldsValue({ task_uuid: task.uuid });
      fillFromTask(task);
      return;
    }

    if (typeof task === "string") {
      const found = tasksOptions.find((t) => t.uuid === task);
      if (found) {
        usedForm.setFieldsValue({ task_uuid: found.uuid });
        fillFromTask(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, tasksOptions, data?.uuid]);

  const showSetMeExecutor = useMemo(() => {
    const access =
      !isExecutor &&
      // Fixme: Возможно эта проверка больше не нужна, теперь у нас есть роли и задачу видят только те, кто в РГ
      // ну а чего бы и нет, пусть админ может назначить на себя задачу, даже если он не в РГ
      // PS. Больше не может, только из списка участников.
      executorsOptions.map((u) => u.uuid).includes(user?.employee_uuid);

    if (!access) return false;

    return access;
  }, [isExecutor, executorsOptions, user]);

  const showSetMeAcceptor = useMemo(() => {
    const access =
      !isAcceptor &&
      // Fixme: Возможно эта проверка больше не нужна, теперь у нас есть роли и задачу видят только те, кто в РГ
      // ну а чего бы и нет, пусть админ может назначить на себя задачу, даже если он не в РГ
      // PS. Больше не может, только из списка участников.
      executorsOptions.map((u) => u.uuid).includes(user?.employee_uuid);

    if (!access) return false;

    return access;
  }, [isAcceptor, executorsOptions, user]);

  const defaultValues = useMemo(
    () => ({
      template_title: "",
      task_uuid: null,
      name: "",
      parent_task_uuid: null,
      description: "",
      unit_uuid: data?.unit_uuid || defaultUnit?.uuid || null,
      start_date: dayjs(),
      due_date: null,
      responsible_executor_uuid: null,
      importance_uuid: defaultPriority?.uuid || null,
      task_type_uuid: defaultTaskType?.uuid || null,
      coexecutors_uuids: [],
      author_uuid: user.employee_uuid,
      is_visible: true,
      is_accessible_for_all_in_unit: false,
      planned_costs: null,
      require_actuals_on_acceptance: false,
    }),
    [data, defaultPriority, defaultUnit, user, defaultTaskType]
  );

  const initialValues = useMemo(
    () => ({
      ...defaultValues,
      ...data,
      coexecutors_uuids: data?.coexecutors?.map((c) => c.uuid) || [],
      start_date: data?.start_date
        ? dayjs(data.start_date)
        : defaultValues.start_date ?? null,
      due_date: data?.due_date ? dayjs(data.due_date) : null,
    }),
    [data, defaultValues]
  );

  const setMeExecutor = async () => {
    setIsExecutor(true);

    usedForm.setFieldsValue({
      responsible_executor_uuid: user.employee_uuid,
    });
  };

  const setMeAcceptor = async () => {
    setIsAcceptor(true);

    usedForm.setFieldsValue({
      assignee_acceptor_uuid: user.employee_uuid,
    });
  };

  const handleTaskChange = async (taskUuid) => {
    const t = tasksOptions.find((x) => x.uuid === taskUuid);
    if (t && !useNewData) {
      await fillFromTask(t);
    }
  };

  const handleUseNewDataChange = (checked) => {
    setUseNewData(checked);

    if (checked) {
      const currentTitle =
        usedForm.getFieldValue("template_title") ??
        defaultValues?.template_title;
      usedForm.setFieldsValue({
        ...defaultValues,
        template_title: currentTitle,
      });
      if (defaultUnit?.uuid) {
        fetchUserList(defaultUnit?.uuid);
      }
    }
  };

  const onValuesChange = async (changedValues) => {
    // Проверяем заголовок шаблона
    if ("template_title" in changedValues) {
      setButtonDisabled(!changedValues.template_title);
    }

    // Проверяем, изменилось ли РГ
    if ("unit_uuid" in changedValues) {
      // сбрасываем исполнителя
      setIsExecutor(false);
      setIsAcceptor(false);

      usedForm.setFieldsValue({
        responsible_executor_uuid: null,
        assignee_acceptor_uuid: null,
        coexecutors_uuids: [],
      });
      // Запрашиваем список новых
      fetchUserList(changedValues.unit_uuid);
    }

    if ("responsible_executor_uuid" in changedValues) {
      setIsExecutor(
        changedValues.responsible_executor_uuid === user.employee_uuid
      );
    }

    if ("assignee_acceptor_uuid" in changedValues) {
      setIsAcceptor(
        changedValues.assignee_acceptor_uuid === user.employee_uuid
      );
    }
  };

  const handleFinish = async (values) => {
    await onSubmit?.({
      ...values,
      description: normalizeDescriptionMarkdown(values.description),
    });
  };

  return (
    <>
      <Form
        id={formId}
        form={usedForm}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={onValuesChange}
        onFinish={handleFinish}
      >
        <Form.Item name="planned_costs" hidden>
          <Input type="number" />
        </Form.Item>

        <Form.Item
          style={{ marginBottom: 12 }}
          name="template_title"
          label="Название шаблона"
          rules={[{ required: true, message: "Введите название шаблона" }]}
        >
          <Input placeholder="Введите название шаблона" />
        </Form.Item>

        <Form.Item
          name="unit_uuid"
          label="Рабочая группа"
          rules={[{ required: true, message: "Выберите рабочую группу" }]}
        >
          <Select
            disabled={!!data?.uuid}
            placeholder="Выберите группу"
            options={units.map((u) => ({ value: u.uuid, label: u.name }))}
          />
        </Form.Item>

        <Card style={{ background: "#F7F7F7", borderColor: "#e9e9e9" }}>
          <Flex vertical gap={12}>
            <div style={{ fontWeight: 500, fontSize: "15px" }}>
              Данные для шаблона
            </div>

            <Form.Item
              name="task_uuid"
              style={{ marginBottom: 0 }}
              hidden={!!data?.uuid}
            >
              <Select
                placeholder="Выбрать из задачи"
                options={tasksOptions.map((t) => ({
                  value: t.uuid,
                  label: `№${t.task_number} ${t.name}`,
                }))}
                onChange={handleTaskChange}
                disabled={useNewData}
              />
            </Form.Item>

            <Button
              type="link"
              size="small"
              onClick={() => setShowDetails((prev) => !prev)}
              style={{ padding: 0, color: "#777777", alignSelf: "start" }}
            >
              {showDetails ? (
                <Flex gap={8}>
                  <EyeOffIcon />
                  Скрыть параметры содержимого
                </Flex>
              ) : (
                <Flex gap={8}>
                  <EyeIcon />
                  Показать параметры содержимого
                </Flex>
              )}
            </Button>

            <Divider
              style={{ borderColor: "#E9E9E9", marginBlock: 0 }}
            ></Divider>

            {useNewData && !data?.uuid && (
              <Checkbox
                checked={useNewData}
                onChange={(e) => handleUseNewDataChange(e.target.checked)}
              >
                Новые данные
              </Checkbox>
            )}

            <Form.Item hidden={!showDetails}>
              <Flex vertical gap={12}>
                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={formSize === "small" ? 24 : 16}>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      name="name"
                      label="Название"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={formSize === "small" ? 24 : 8}>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      name="task_type_uuid"
                      label="Тип задачи"
                      rules={[{ required: true, message: "Обязательное поле" }]}
                    >
                      <CustomSelect
                        showSearch
                        options={taskTypesOptions}
                        fieldNames={{ label: "name", value: "uuid" }}
                        optionFilterProp="name"
                        popupMatchSelectWidth={false}
                        placement="bottomRight"
                        labelRender={(props) => {
                          const { label, value } = props;

                          const taskType = taskTypes.find(
                            (taskType) => taskType.uuid === value
                          );

                          const icon = TASK_TYPE_OPTIONS.find(
                            (item) => item.value === taskType.key
                          )?.icon;

                          return (
                            <Tag icon={icon} className="tag-with-icon lg">
                              {label}
                            </Tag>
                          );
                        }}
                        optionRender={(option) => {
                          const icon = TASK_TYPE_OPTIONS.find(
                            (item) => item.value === option.data.key
                          )?.icon;

                          return (
                            <Tag icon={icon} className="tag-with-icon lg">
                              {option.label}
                            </Tag>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="parent_task_uuid"
                  label="Родительская задача"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Выберите задачу"
                    options={tasksOptions.map((t) => ({
                      value: t.uuid,
                      label: `№${t.task_number} ${t.name}`,
                    }))}
                    allowClear
                  />
                </Form.Item>

                <Form.Item name="description" label="Описание">
                  <MarkdownEditor rows={4} />
                </Form.Item>

                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={24} md={formSize === "small" ? 24 : 12}>
                    <Form.Item
                      name="start_date"
                      label="Дата начала"
                      style={{ marginBottom: 0 }}
                    >
                      <DigitsDatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={formSize === "small" ? 24 : 12}>
                    <Form.Item
                      name="due_date"
                      label="Срок исполнения"
                      style={{ marginBottom: 0 }}
                    >
                      <DigitsDatePicker
                        type="moment"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 12]}>
                  <Col xs={24}>
                    <Form.Item
                      name="require_actuals_on_acceptance"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Обязательное заполнение трудозатрат</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={24} md={formSize === "small" ? 24 : 16}>
                    <AssignTo
                      showAction={showSetMeExecutor}
                      onChange={setMeExecutor}
                    >
                      <Form.Item
                        name="responsible_executor_uuid"
                        label="Исполнитель"
                        style={{ marginBottom: 0 }}
                      >
                        <CustomSelect
                          placeholder="Начните вводить"
                          options={executorsOptions}
                          showSearch
                          allowClear
                          fieldNames={{ value: "uuid" }}
                          optionFilterProp="label"
                          onChange={(value) => {
                            usedForm.setFieldsValue({
                              responsible_executor_uuid: value || null, // Устанавливаем null, если значение очищено
                            });
                          }}
                        />
                      </Form.Item>
                    </AssignTo>
                  </Col>
                  <Col xs={24} sm={24} md={formSize === "small" ? 24 : 8}>
                    <Form.Item
                      name="importance_uuid"
                      label="Приоритет"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: "Обязательное поле" }]}
                    >
                      <CustomSelect
                        showSearch
                        options={prioritiesOptions}
                        fieldNames={{ label: "name", value: "uuid" }}
                        optionFilterProp="name"
                        labelRender={(props) => {
                          const { label, value } = props;

                          const priority = priorities.find(
                            (priority) => priority.uuid === value
                          );

                          const icon = PRIORITY_OPTIONS.find(
                            (item) => item.value === priority.color
                          )?.icon;

                          return (
                            <Tag icon={icon} className="tag-with-icon">
                              {label}
                            </Tag>
                          );
                        }}
                        optionRender={(option) => {
                          const icon = PRIORITY_OPTIONS.find(
                            (item) => item.value === option.data.color
                          )?.icon;

                          return (
                            <Tag icon={icon} className="tag-with-icon">
                              {option.label}
                            </Tag>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Col xs={24}>
                  <Form.Item
                    name="coexecutors_uuids"
                    label="Соисполнители"
                    style={{ marginBottom: 0 }}
                  >
                    <CustomSelect
                      placeholder="Начните вводить"
                      options={soExecutorsOptions}
                      mode="multiple"
                      showSearch
                      allowClear
                      fieldNames={{ value: "uuid" }}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <AssignTo
                    showAction={showSetMeAcceptor}
                    onChange={setMeAcceptor}
                  >
                    <Form.Item
                      name="assignee_acceptor_uuid"
                      label="Приемщик"
                      style={{ marginBottom: 0 }}
                    >
                      <CustomSelect
                        placeholder="Начните вводить"
                        options={executorsOptions}
                        showSearch
                        allowClear
                        fieldNames={{ value: "uuid" }}
                        optionFilterProp="label"
                        onChange={(value) => {
                          usedForm.setFieldsValue({
                            assignee_acceptor_uuid: value || null, // Устанавливаем null, если значение очищено
                          });
                        }}
                      />
                    </Form.Item>
                  </AssignTo>
                </Col>
              </Flex>
            </Form.Item>

            {showDetails && !useNewData && !data?.uuid && (
              <Divider
                style={{ borderColor: "#E9E9E9", marginBlock: 0 }}
              ></Divider>
            )}

            {!useNewData && !data?.uuid && (
              <Checkbox
                checked={useNewData}
                onChange={(e) => handleUseNewDataChange(e.target.checked)}
              >
                Новые данные
              </Checkbox>
            )}
          </Flex>
        </Card>
        {showSubmit && (
          <Form.Item noStyle shouldUpdate>
            {() => (
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={buttonDisabled}
                >
                  Сохранить
                </Button>
              </div>
            )}
          </Form.Item>
        )}
      </Form>
    </>
  );
}

export default TaskTemplateForm;
